/**
 * Root App component
 * Layout wrapper + routes + analytics
 * @module App
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AppRoutes from './app/routes';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import GoogleAnalytics from './components/analytics/GoogleAnalytics';
import CookieConsent from './components/ui/CookieConsent';
import OfflineIndicator from './components/ui/OfflineIndicator';
import LanguageFallbackIndicator from './components/ui/LanguageFallbackIndicator';
import FloatingWhatsApp from './components/chatfunnel/FloatingWhatsApp';
import { finishAuthBootstrap, loadUser } from './features/auth/authSlice';
import { fetchNavItems } from './features/nav/navSlice';
import { fetchBranding } from './features/branding/brandingSlice';
import BrandHead from './features/branding/BrandHead';
import { fetchSiteSettings } from './features/site-settings/siteSettingsSlice';
import { fetchMaintenanceStatus } from './features/maintenance/maintenanceSlice';
import MaintenancePage from './pages/MaintenancePage';

const PlanYourJourneyModal = React.lazy(() => import('./components/ui/PlanYourJourneyModal'));

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isAuthRoute =
    /^\/(?:[a-z]{2}\/)?(?:login|register)(?:\/|$)/i.test(location.pathname);
  const hasStoredSession = Boolean(localStorage.getItem('token') || localStorage.getItem('mvp_token'));

  const { user } = useSelector((s) => s.auth);
  const { isEnabled: maintenanceActive, estimatedEnd } = useSelector((s) => s.maintenance);
  const isDeveloper = user?.role === 'developer';

  // Load user on app start; only fetch public nav for non-admin paths
  useEffect(() => {
    if (hasStoredSession) {
      dispatch(loadUser());
    } else {
      dispatch(finishAuthBootstrap());
    }
    dispatch(fetchBranding());
    dispatch(fetchSiteSettings());
    dispatch(fetchMaintenanceStatus());
    if (!isAdmin) dispatch(fetchNavItems());
  }, [dispatch, isAdmin, hasStoredSession]);

  // Re-check maintenance status when the configured end time is reached.
  useEffect(() => {
    if (!maintenanceActive || !estimatedEnd) return undefined;

    const endAt = new Date(estimatedEnd).getTime();
    if (Number.isNaN(endAt)) return undefined;

    const delay = Math.max(endAt - Date.now(), 0) + 1000;
    const timer = window.setTimeout(() => {
      dispatch(fetchMaintenanceStatus());
    }, delay);

    return () => window.clearTimeout(timer);
  }, [dispatch, maintenanceActive, estimatedEnd]);

  // Show maintenance page for non-developer users when maintenance mode is active
  if (maintenanceActive && !isDeveloper && !isAuthRoute) {
    return <MaintenancePage />;
  }

  // Admin routes get their own full-screen layout (AdminLayout)
  if (isAdmin) {
    return (
      <>
        <GoogleAnalytics />
        <BrandHead />
        <OfflineIndicator />
        <React.Suspense fallback={null}>
          <PlanYourJourneyModal />
        </React.Suspense>
        <AppRoutes />
      </>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <GoogleAnalytics />
      <BrandHead />
      <OfflineIndicator />
      <React.Suspense fallback={null}>
        <PlanYourJourneyModal />
      </React.Suspense>
      <Header />
      <LanguageFallbackIndicator />
      <main className="flex-grow">
        <AppRoutes />
      </main>
      <Footer />
      <FloatingWhatsApp />
      <CookieConsent />
    </div>
  );
};

export default App;

// Developed and Designed by Trophy Developers
